import React, {
  forwardRef,
  useRef,
  useEffect,
  type ReactNode,
  type HTMLAttributes,
} from "react";
import InputNumber, { type InputNumberProps } from "rc-input-number";
import { useComposedRefs } from "@exonn/hooks";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage.tsx";
import * as styles from "./NumberInput.style.ts";
import type { ValueType } from "rc-input-number";
import type { ForwardedRef } from "react";
import { BaseInputV2AdornmentContext } from "src/components/BaseInputV2/BaseInputV2AdornmentContext.tsx";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

export interface NumberInputProps<T extends ValueType = ValueType>
  extends InputNumberProps<T> {
  label?: string;
  helperText?: ReactNode;
  error?: string;
  unit?: string;
  isLoading?: boolean;
  additionalLabelElems?: ReactNode[];
  hideErrorText?: boolean;
  rootProps?: HTMLAttributes<HTMLDivElement>;
  className?: string;
  floatingError?: boolean;
  endAdornment?: ReactNode;
  width?: number;
}

const NumberInputComponent = <T extends ValueType = ValueType>(
  props: NumberInputProps<T>,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const {
    label,
    helperText,
    required,
    additionalLabelElems,
    error,
    isLoading = false,
    onStep: onStepProp,
    width,
    unit,
    disabled = false,
    decimalSeparator = ",",
    step,
    hideErrorText = false,
    value,
    rootProps,
    floatingError = false,
    onPointerLeave,
    onPointerEnter,
    endAdornment,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const composedRef = useComposedRefs(inputRef, ref);

  const formatter = (
    value: ValueType | undefined,
    info: {
      input: string;
      userTyping: boolean;
    },
  ): string => {
    const [intPart, decimalPart] = `${value}`.toString().split(".");

    const formattedIntPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    return decimalPart
      ? formattedIntPart + decimalSeparator + decimalPart
      : formattedIntPart;
  };

  const handleStep = (
    value: T,
    info: {
      offset: ValueType;
      type: "up" | "down";
    },
  ) => {
    if (disabled) {
      return;
    }
    onStepProp?.(value, info);
  };

  useEffect(() => {
    if (inputRef.current) {
      const unitEl = inputRef.current.parentNode?.querySelector(".unit");

      if (!unitEl && unit) {
        const elem = document.createElement("span");
        elem.textContent = unit;
        elem.className = "unit";
        inputRef.current.insertAdjacentElement("afterend", elem);
      } else if (unitEl && unit && unitEl?.textContent !== unit) {
        unitEl.textContent = unit;
      } else if (unitEl && !unit) {
        unitEl.remove();
      }
    }
  }, [unit]);

  return (
    <Skeleton isLoading={isLoading}>
      <div
        {...rootProps}
        css={styles.rootContainer({
          width,
        })}
      >
        {label && (
          <InputLabel
            label={label}
            required={required}
            helperText={helperText}
            additionalElems={additionalLabelElems}
          />
        )}
        <div
          style={{
            position: "relative",
          }}
          onPointerLeave={onPointerLeave}
          onPointerEnter={onPointerEnter}
        >
          <InputNumber
            ref={composedRef}
            css={styles.rootNumberInputStyle({
              error,
              disabled,
              hasEndAdornment: !!endAdornment,
            })}
            onStep={handleStep}
            changeOnWheel
            upHandler={<i className={"icon-15-arw_up"} />}
            downHandler={<i className={"icon-15-arw_dw"} />}
            decimalSeparator={decimalSeparator}
            formatter={formatter}
            disabled={disabled}
            step={step}
            value={value}
            {...rest}
          />
          <div
            style={{
              position: "absolute",
              right: 1,
              top: 1,
              bottom: 1,
            }}
          >
            <BaseInputV2AdornmentContext.Provider
              value={{
                position: "end",
                disabled,
              }}
            >
              {endAdornment}
            </BaseInputV2AdornmentContext.Provider>
          </div>
        </div>

        {error && !disabled && !hideErrorText && (
          <InputErrorMessage floating={floatingError} message={error} />
        )}
      </div>
    </Skeleton>
  );
};

export const NumberInput = forwardRef(NumberInputComponent);
